import { ArrowRight, Shield, Zap, BarChart3 } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

interface HeroContent {
  title: string;
  subtitle: string;
  description: string;
  badge: string;
  cta_primary: string;
  cta_secondary: string;
  stats: { value: string; label: string }[];
}

const defaultContent: HeroContent = {
  title: '船运发货',
  subtitle: '一码通系统',
  description: '郑州博乐为企业打造的全流程船运物流管理平台，实现从派车调度到计量结算的数字化智能化管理。支持煤炭、化工、水泥、矿山、粮油等行业的智慧物流解决方案。',
  badge: '智能化船运物流解决方案',
  cta_primary: '了解产品',
  cta_secondary: '预约演示',
  stats: [
    { value: '99.9%', label: '系统稳定性' },
    { value: '50%+', label: '效率提升' },
    { value: '1000+', label: '服务企业' },
  ],
};

export default function Hero() {
  const { content } = useSiteContent('hero');
  const data: HeroContent = content?.content || defaultContent;

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-ocean-900">
        <div className="absolute top-20 left-10 w-72 h-72 bg-ocean-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-ocean-400/10 rounded-full blur-3xl" />
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="w-2 h-2 bg-ocean-400 rounded-full animate-pulse" />
              <span className="text-ocean-300 text-sm font-medium">{data.badge}</span>
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                <span itemProp="name">{data.title}</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-ocean-400 to-primary-400" itemProp="applicationCategory">
                  {data.subtitle}
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 max-w-xl leading-relaxed" itemProp="description">
                {data.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-6 sm:gap-8">
              {(data.stats || defaultContent.stats).map((stat: { value: string; label: string }) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => scrollToSection('#products')}
                className="group px-8 py-4 bg-gradient-to-r from-ocean-500 to-primary-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-ocean-500/30 transition-all duration-300 flex items-center gap-2"
              >
                {data.cta_primary}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection('#contact')}
                className="px-8 py-4 bg-white text-slate-700 font-semibold rounded-xl border border-slate-200 hover:border-ocean-300 hover:bg-ocean-50 transition-all duration-300"
              >
                {data.cta_secondary}
              </button>
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="relative">
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <div className="flex items-center gap-2 mb-6">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 text-center text-slate-400 text-sm">船运发货一码通系统</div>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { label: '今日发货', value: '128', unit: '船次', color: 'from-ocean-400 to-ocean-500' },
                      { label: '累计吨位', value: '15,680', unit: '吨', color: 'from-primary-400 to-primary-500' },
                      { label: '在途船只', value: '42', unit: '艘', color: 'from-emerald-400 to-emerald-500' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-slate-700/50 rounded-xl p-4">
                        <div className={`text-2xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                          {stat.value}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">{stat.label}</div>
                        <div className="text-xs text-slate-500">{stat.unit}</div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-slate-700/30 rounded-xl p-4 h-32">
                    <div className="flex items-end justify-between h-full gap-2">
                      {[40, 65, 45, 80, 55, 90, 70, 85, 60, 75, 50, 95].map((height, i) => (
                        <div
                          key={i}
                          className="flex-1 bg-gradient-to-t from-ocean-500/60 to-ocean-400/80 rounded-t"
                          style={{ height: `${height}%` }}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {['豫信货1688 - 装货中', '郑运达8899 - 运输中', '中原船务666 - 待卸货'].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 bg-slate-700/30 rounded-lg p-3">
                        <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-ocean-400' : 'bg-emerald-400'}`} />
                        <span className="text-sm text-slate-300 flex-1">{item}</span>
                        <span className={`text-xs px-2 py-1 rounded ${i === 0 ? 'bg-yellow-400/20 text-yellow-300' : i === 1 ? 'bg-ocean-400/20 text-ocean-300' : 'bg-emerald-400/20 text-emerald-300'}`}>
                          {i === 0 ? '装货中' : i === 1 ? '运输中' : '待卸货'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-xl animate-float" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-ocean-100 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-ocean-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800 text-sm">安全可靠</div>
                    <div className="text-xs text-slate-500">数据加密保护</div>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800 text-sm">高效便捷</div>
                    <div className="text-xs text-slate-500">扫码快速通行</div>
                  </div>
                </div>
              </div>

              <div className="absolute top-1/2 -right-8 bg-white rounded-xl p-4 shadow-xl animate-float" style={{ animationDelay: '1.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800 text-sm">智能分析</div>
                    <div className="text-xs text-slate-500">实时数据报表</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 88L1440 86V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
}
